import { doc, runTransaction } from 'firebase/firestore'
import { db } from '../store/firebase'
import { Medicine, MedicineBatch, StockBatchAllocation, StockMovement, User } from '../types'
import { BillLine, BillingAccount, BillingConfig, BillingResult, CatalogueItem, DirectSale, ItemStockMove, SaleTender } from '../types/billing'
import { BCOL, clean, commitNumbers, failure, planNumbers, writeAudit } from './billingFirestore'
import { calculateLine, documentTotals, fromN, makeId, toN, todayLusaka } from './billingMoney'

const MEDICINES_COL = 'medicines'
const BATCHES_COL = 'medicineBatches'
const MOVEMENTS_COL = 'stockMovements'

export type DirectSaleInput = {
  id: string
  account: BillingAccount | null
  walkInName?: string
  phone?: string
  lines: BillLine[]
  tenders: { methodId: string; amount: number; reference?: string }[]
  cashTendered: number
  notes?: string
}

export type SaleRules = { canEditPrices: boolean; maxDiscount: number; items: CatalogueItem[]; batches: MedicineBatch[] }

/** Validates a direct sale exactly as it will be saved. Returns the priced lines, totals and tenders. */
export function validateDirectSale(config: BillingConfig, input: DirectSaleInput, rules: SaleRules) {
  if (!config.sales.directSalesEnabled) throw new Error('Direct receipts are switched off in Billing Settings → Invoices & direct sales.')
  if (!input.account) {
    if (!config.sales.walkInEnabled) throw new Error('Walk-in sales are switched off. Choose a patient or customer account.')
    if (config.sales.requireCustomerName && !input.walkInName?.trim()) throw new Error('Enter the customer name.')
  }
  if (config.sales.requirePhone && !(input.phone?.trim() || input.account?.phone)) throw new Error('A phone number is required for sales.')
  if (!input.lines.length) throw new Error('Add at least one item or service.')

  const lines = input.lines.map((raw) => {
    const line = calculateLine({ ...raw, fixed: false })
    const label = line.description || 'A line'
    if (!line.description.trim()) throw new Error('Every line needs a description.')
    if (!(line.quantity > 0)) throw new Error(`${label}: quantity must be more than zero.`)
    if (toN(line.unitPrice) <= 0) throw new Error(`${label}: price must be more than zero.`)
    if (line.discountPct > rules.maxDiscount) throw new Error(`${label}: discounts above ${rules.maxDiscount}% need approval from a user with discount-approval permission.`)
    const item = line.itemId ? rules.items.find((i) => i.id === line.itemId) : undefined
    if (line.itemId && (!item || item.status !== 'Active')) throw new Error(`${label} is no longer an active item.`)
    if (!rules.canEditPrices) {
      if (!item) throw new Error(`${label} is not in Items & Services. Your role can only sell catalogue items.`)
      if (toN(item.unitPrice) !== toN(line.unitPrice)) throw new Error(`Your role cannot change the price of ${label}.`)
    }
    if (item && !item.discountAllowed && line.discountPct > 0) throw new Error(`${label} does not allow discounts.`)
    return line
  })
  const totals = documentTotals(lines)
  if (toN(totals.total) <= 0) throw new Error('The sale total must be more than zero.')

  const enabled = config.paymentMethods.filter((m) => m.enabled)
  const tenderRows = input.tenders.filter((t) => toN(t.amount) > 0)
  if (!tenderRows.length) throw new Error('Enter how the customer paid.')
  if (tenderRows.length > 1 && !config.sales.splitTenders) throw new Error('Split payments are switched off in Billing Settings → Payments.')
  const tenders: SaleTender[] = tenderRows.map((t) => {
    const method = enabled.find((m) => m.id === t.methodId)
    if (!method) throw new Error('A payment method is not enabled.')
    if (method.requiresReference && !t.reference?.trim()) throw new Error(`${method.name} needs a transaction reference.`)
    return { methodId: method.id, methodName: method.name, amount: fromN(toN(t.amount)), reference: t.reference?.trim() || undefined }
  })
  if (tenders.filter((t) => t.methodId === 'cash').length > 1) throw new Error('Combine cash into one payment line.')
  const paidN = tenders.reduce((s, t) => s + toN(t.amount), 0)
  if (paidN < toN(totals.total)) throw new Error(`The sale is not fully paid (${fromN(toN(totals.total) - paidN).toFixed(2)} short). Use an invoice if a balance will remain.`)
  if (paidN > toN(totals.total)) throw new Error('Payments add up to more than the total. Enter the cash handed over under "Cash tendered" instead.')
  const cash = tenders.find((t) => t.methodId === 'cash')
  const cashN = cash ? toN(cash.amount) : 0
  const tenderedN = cash ? Math.max(toN(input.cashTendered) || cashN, cashN) : 0
  if (cash && toN(input.cashTendered) > 0 && toN(input.cashTendered) < cashN) throw new Error('Cash tendered is less than the cash due.')

  // Stock: total quantity per linked medicine, allocated FEFO from unexpired batches.
  const stock = new Map<string, { qty: number; name: string }>()
  /** Billing-inventory items (stock items not linked to Pharmacy): item id → quantity sold. */
  const itemStock = new Map<string, number>()
  for (const line of lines) {
    const item = line.itemId ? rules.items.find((i) => i.id === line.itemId) : undefined
    // Billing inventory is independent of Pharmacy Inventory: cash sales never touch medicine stock.
    if (item?.kind === 'Stock') {
      if (!Number.isInteger(line.quantity)) throw new Error(`${line.description}: stock items are sold in whole units.`)
      itemStock.set(item.id, (itemStock.get(item.id) ?? 0) + line.quantity)
    }
  }
  return { lines, totals, tenders, cashTenderedN: tenderedN, changeN: tenderedN - cashN, stock, itemStock }
}

/**
 * Saves a cash sale in one transaction: receipt number, sale record, stock
 * deduction for linked stock items, stock movements and audit. The client key
 * makes retries and double-clicks return the original sale.
 */
export async function recordDirectSale(user: User, config: BillingConfig, input: DirectSaleInput, rules: SaleRules): Promise<BillingResult> {
  try {
    const v = validateDirectSale(config, input, rules)
    const today = todayLusaka()
    const result = await runTransaction(db, async (tx) => {
      const saleRef = doc(db, BCOL.sales, input.id)
      const existing = await tx.get(saleRef)
      if (existing.exists()) return { already: true, number: (existing.data() as DirectSale).number }

      // ── READS ──
      const stockPlans: { medicine: Medicine; batches: MedicineBatch[]; qty: number; name: string }[] = []
      for (const [medicineId, need] of v.stock) {
        const medSnap = await tx.get(doc(db, MEDICINES_COL, medicineId))
        if (!medSnap.exists()) throw new Error(`${need.name} is no longer in Pharmacy Inventory.`)
        const candidates = rules.batches.filter((b) => b.medicineId === medicineId)
        const snaps = await Promise.all(candidates.map((b) => tx.get(doc(db, BATCHES_COL, b.id))))
        const batches = snaps
          .filter((s) => s.exists())
          .map((s) => ({ ...(s.data() as MedicineBatch), id: s.id }))
          .filter((b) => Number(b.quantityRemaining) > 0 && !(b.expiryDate && b.expiryDate.slice(0, 10) < today))
          .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))
        const available = batches.reduce((s, b) => s + Number(b.quantityRemaining), 0)
        if (available < need.qty) throw new Error(`Only ${available} unexpired unit(s) of ${need.name} in stock — ${need.qty} needed.`)
        stockPlans.push({ medicine: { ...(medSnap.data() as Medicine), id: medSnap.id }, batches, qty: need.qty, name: need.name })
      }
      const itemPlans: { item: CatalogueItem; qty: number }[] = []
      for (const [itemId, qty] of v.itemStock) {
        const snap = await tx.get(doc(db, BCOL.items, itemId))
        const item = snap.data() as CatalogueItem | undefined
        if (!item) throw new Error('An item on this sale no longer exists.')
        const onHand = Number(item.quantityOnHand) || 0
        if (onHand < qty) throw new Error(`Only ${onHand} ${item.unit ?? 'unit(s)'} of ${item.name} in inventory — ${qty} needed. Receive stock first.`)
        itemPlans.push({ item, qty })
      }
      const plan = await planNumbers(tx, config, 'saleReceipt', 1)

      // ── WRITES ──
      const now = new Date().toISOString()
      const number = plan.numbers[0]
      commitNumbers(tx, plan, [input.id])
      const movementIds: string[] = []
      for (const sp of stockPlans) {
        let remaining = sp.qty
        const allocations: StockBatchAllocation[] = []
        for (const b of sp.batches) {
          if (remaining <= 0) break
          const take = Math.min(remaining, Number(b.quantityRemaining))
          allocations.push({ batchId: b.id, batchNumber: b.batchNumber, quantity: -take, quantityBefore: Number(b.quantityRemaining), quantityAfter: Number(b.quantityRemaining) - take })
          remaining -= take
        }
        const onHand = sp.batches.reduce((s, b) => s + Number(b.quantityRemaining), 0)
        const before = Math.max(Number(sp.medicine.currentQuantity) || 0, onHand)
        const movementId = makeId('movement')
        movementIds.push(movementId)
        allocations.forEach((a) => tx.update(doc(db, BATCHES_COL, a.batchId), { quantityRemaining: a.quantityAfter, quantity: a.quantityAfter }))
        tx.update(doc(db, MEDICINES_COL, sp.medicine.id), { currentQuantity: before - sp.qty, updatedAt: now })
        const movement: StockMovement = {
          id: movementId, medicineId: sp.medicine.id, medicineName: sp.medicine.name || sp.name,
          batchId: allocations[0]?.batchId, batchNumber: allocations[0]?.batchNumber, allocations,
          type: 'Dispense', quantityDelta: -sp.qty, quantityBefore: before, quantityAfter: before - sp.qty,
          referenceType: 'Direct Sale', referenceId: input.id, actionPerformed: 'Billing Direct Sale',
          notes: `Sold on direct receipt ${number}. Billed on the receipt — not a pharmacy charge.`,
          actorId: user.id, actorName: user.fullName, createdAt: now,
        }
        tx.set(doc(db, MOVEMENTS_COL, movementId), clean(movement))
      }
      const itemStockMoves: { itemId: string; qty: number }[] = []
      for (const { item, qty } of itemPlans) {
        const before = Number(item.quantityOnHand) || 0
        const moveId = makeId('smove')
        tx.update(doc(db, BCOL.items, item.id), { quantityOnHand: before - qty, updatedAt: now })
        tx.set(doc(db, BCOL.stockMoves, moveId), clean({ id: moveId, itemId: item.id, itemCode: item.code, itemName: item.name, type: 'Sale', quantity: -qty, before, after: before - qty, reference: number, byName: user.fullName, at: now } satisfies ItemStockMove))
        itemStockMoves.push({ itemId: item.id, qty })
      }
      const sale: DirectSale = {
        itemStock: itemStockMoves,
        id: input.id, number, state: 'Completed',
        accountId: input.account?.id, accountName: input.account?.name ?? input.walkInName?.trim() ?? 'Walk-in customer',
        accountType: input.account?.type ?? 'WalkIn', patientId: input.account?.patientId, bmcFileNumber: input.account?.bmcFileNumber,
        phone: input.phone?.trim() || input.account?.phone, date: today,
        lines: v.lines, ...v.totals, tenders: v.tenders, cashTendered: fromN(v.cashTenderedN), changeGiven: fromN(v.changeN),
        stockMovementIds: movementIds, notes: input.notes?.trim() || undefined,
        issuer: { business: config.business, appearance: { ...config.appearance, paperSize: 'A4' }, regional: config.regional },
        receiptFooter: config.receipts.footer, cashierId: user.id, cashierName: user.fullName, createdAt: now,
      }
      tx.set(saleRef, clean(sale))
      writeAudit(tx, user, { action: 'Direct sale recorded', entity: 'Direct sale', entityId: input.id, reference: number, summary: `${sale.accountName} — ${sale.total.toFixed(2)} (${v.tenders.map((t) => t.methodName).join(' + ')})` })
      return { already: false, number }
    })
    return { ok: true, id: input.id, message: result.already ? `Already saved as ${result.number}.` : `Receipt ${result.number} issued.` }
  } catch (error) {
    return failure(error, 'The sale was not saved. Nothing was recorded — you can retry safely.')
  }
}

/** Voids a direct sale: it stays on record marked VOID, and any stock it took is returned to the same batches. */
export async function voidDirectSale(user: User, id: string, reason: string): Promise<BillingResult> {
  try {
    if (!reason.trim()) return { ok: false, error: 'A reason is required to void a sale.' }
    await runTransaction(db, async (tx) => {
      const ref = doc(db, BCOL.sales, id)
      const sale = (await tx.get(ref)).data() as DirectSale | undefined
      if (!sale) throw new Error('Sale not found.')
      if (sale.state === 'Void') return
      const movements = (await Promise.all(sale.stockMovementIds.map((m) => tx.get(doc(db, MOVEMENTS_COL, m))))).filter((s) => s.exists()).map((s) => ({ ...(s.data() as StockMovement), id: s.id }))
      const medSnaps = await Promise.all(movements.map((m) => tx.get(doc(db, MEDICINES_COL, m.medicineId))))
      const batchIds = [...new Set(movements.flatMap((m) => (m.allocations ?? []).map((a) => a.batchId)))]
      const batchSnaps = await Promise.all(batchIds.map((b) => tx.get(doc(db, BATCHES_COL, b))))
      const itemSnaps = await Promise.all((sale.itemStock ?? []).map((s) => tx.get(doc(db, BCOL.items, s.itemId))))
      const batchQty = new Map(batchSnaps.filter((s) => s.exists()).map((s) => [s.id, Number((s.data() as MedicineBatch).quantityRemaining) || 0]))
      const existingBatches = new Set(batchQty.keys())
      const now = new Date().toISOString()
      const reversalIds: string[] = []
      movements.forEach((m, i) => {
        const med = medSnaps[i].data() as Medicine | undefined
        const before = Number(med?.currentQuantity) || 0
        const qty = Math.abs(m.quantityDelta)
        const allocations: StockBatchAllocation[] = (m.allocations ?? []).map((a) => {
          const cur = batchQty.get(a.batchId) ?? 0
          batchQty.set(a.batchId, cur + Math.abs(a.quantity))
          return { batchId: a.batchId, batchNumber: a.batchNumber, quantity: Math.abs(a.quantity), quantityBefore: cur, quantityAfter: cur + Math.abs(a.quantity) }
        })
        allocations.forEach((a) => existingBatches.has(a.batchId) && tx.update(doc(db, BATCHES_COL, a.batchId), { quantityRemaining: a.quantityAfter, quantity: a.quantityAfter }))
        if (med) tx.update(doc(db, MEDICINES_COL, m.medicineId), { currentQuantity: before + qty, updatedAt: now })
        const rid = makeId('movement')
        reversalIds.push(rid)
        tx.set(doc(db, MOVEMENTS_COL, rid), clean({
          id: rid, medicineId: m.medicineId, medicineName: m.medicineName, batchId: allocations[0]?.batchId, batchNumber: allocations[0]?.batchNumber, allocations,
          type: 'Return In', quantityDelta: qty, quantityBefore: before, quantityAfter: before + qty, referenceType: 'Direct Sale', referenceId: id,
          reversalOfMovementId: m.id, actionPerformed: 'Billing Direct Sale Void', notes: `Returned to stock — receipt ${sale.number} voided: ${reason.trim()}`,
          actorId: user.id, actorName: user.fullName, createdAt: now,
        } satisfies StockMovement))
      })
      ;(sale.itemStock ?? []).forEach((s, i) => {
        const item = itemSnaps[i].data() as CatalogueItem | undefined
        if (!item) return
        const before = Number(item.quantityOnHand) || 0
        const moveId = makeId('smove')
        tx.update(doc(db, BCOL.items, s.itemId), { quantityOnHand: before + s.qty, updatedAt: now })
        tx.set(doc(db, BCOL.stockMoves, moveId), clean({ id: moveId, itemId: s.itemId, itemCode: item.code, itemName: item.name, type: 'Sale voided', quantity: s.qty, before, after: before + s.qty, reference: sale.number, reason: reason.trim(), byName: user.fullName, at: now } satisfies ItemStockMove))
      })
      tx.update(ref, { state: 'Void', voidedAt: now, voidedByName: user.fullName, voidReason: reason.trim(), reversalMovementIds: reversalIds })
      writeAudit(tx, user, { action: 'Direct sale voided', entity: 'Direct sale', entityId: id, reference: sale.number, summary: `${sale.accountName} — ${sale.total.toFixed(2)}${reversalIds.length ? `; ${reversalIds.length} stock item(s) returned` : ''}`, reason: reason.trim() })
    })
    return { ok: true, id, message: 'Sale voided. Any stock it used has been returned.' }
  } catch (error) {
    return failure(error, 'The sale could not be voided.')
  }
}
